import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import '../backlog/backlog.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Default Settings → Screen Auto Lock
//
// Ported from ALPACA's XAF form (Modules/BusinessObjects/Setting/ScreenAutoLock.cs).
// Despite the name it does not lock the screen: ALPACA's Global.asax Session_Start
// reads this row and sets the ASP.NET session timeout, so a user with no activity
// for that long is signed out.
//
// Backed by the SHARED single-row dbo.ScreenAutoLock — the same row ALPACA reads,
// so a change here changes ALPACA too (CLAUDE.md §5.0).
//
// The choices come from the API rather than being hard-coded here: ALPACA stores
// an ORDINAL enum, so the list and its order are load-bearing and must have one
// source of truth.
//
// 2026-07-30 — this row is now the ONE application-wide inactivity timeout. It
// drives the SPA's idle sign-out AND every concurrent-user session path (seat
// reservation, per-request validation, heartbeat, idle sweep). The separate
// 5-minute SessionIdleTimeoutMinutes that Client Version Control used to own is
// gone: being checked on every request, it silently overrode whatever was chosen
// here whenever the heartbeat gapped (sleep, lid, VPN drop).
// ─────────────────────────────────────────────────────────────────────────────

interface Cfg { oid: string | null; minutes: number; exists: boolean; choices: number[] }

export default function ScreenAutoLockPage() {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [cfg, setCfg] = useState<Cfg | null>(null);
  const [minutes, setMinutes] = useState<number>(60);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: Cfg }>('/api/v1/screen-auto-lock');
      if (res.data.success) { setCfg(res.data.data); setMinutes(res.data.data.minutes); }
    } catch { notify.error('Could not load Screen Auto Lock.'); }
    finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // gate.cancel(revert) so Cancel also leaves edit mode (owner report 2026-10-12).
  const handleCancel = () => gate.cancel(() => { if (cfg) setMinutes(cfg.minutes); });

  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Screen Auto Lock',
      successMessage: 'Screen Auto Lock saved successfully.',
      save: async () => { await api.put('/api/v1/screen-auto-lock', { minutes }); },
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change Screen Auto Lock.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  const choices = cfg?.choices ?? [30, 60, 90, 120, 180, 240, 300, 360];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Default Settings</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Screen Auto Lock</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="screenautolock" auditFormName="Screen Auto Lock"
              loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Shared with ALPACA — a change here applies to both products.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<EditGateActions gate={gate} saving={saving} disabled={loading}
              onSave={handleSave} onCancel={handleCancel} />}
            onRefresh={() => void load()} refreshing={loading} refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 32px' }}>
            <div style={{ maxWidth: 720 }}>

              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '6px 0 14px' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)', minWidth: 110, paddingTop: 5 }}>
                  Time Out:
                </div>
                <div style={{ flex: 1 }}>
                  {ro ? (
                    <span style={{ fontSize: 13, color: 'var(--ai-ink)', fontWeight: 600 }}>
                      {minutes} Minutes
                    </span>
                  ) : (
                    // Radio list, matching ALPACA's RadioButtonListTimeoutEnumPropertyEditor —
                    // a fixed set of choices, not a free number, because the value is
                    // persisted as an enum ordinal.
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {choices.map((m) => (
                        <label key={m} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13,
                          color: m === minutes ? 'var(--ai-green)' : 'var(--ai-ink)',
                          fontWeight: m === minutes ? 700 : 400, cursor: 'pointer',
                        }}>
                          <input type="radio" name="screen-auto-lock" checked={m === minutes}
                            onChange={() => setMinutes(m)}
                            style={{ accentColor: m === minutes ? 'var(--ai-green)' : 'var(--ai-muted)' }} />
                          {m} Minutes
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
